/**
 * Shortcodes de emoji: además del glifo (😃), el usuario puede escribir
 * `:smiley:`, `(smiley)` o `{smiley}` y se pinta el emoji de SuCom. La clave es el slug
 * de la reacción y su etiqueta normalizada (sin acentos, espacios → `_`).
 */
import { isEmojiGlyph, splitTextForReactions } from "./emoji-split";
import type { EmojiReactionMap, ReactionDefinition } from "./types";

export type ShortcodeReactionMap = ReadonlyMap<string, ReactionDefinition>;

export type ReactionToken = { text: string; reaction?: ReactionDefinition };

const SHORTCODE_RE =
  /:([a-z0-9_+-]{2,40}):|\(([a-z0-9_+-]{2,40})\)|\{([a-z0-9_+-]{2,40})\}/gi;

function matchKey(match: RegExpExecArray): string {
  return (match[1] ?? match[2] ?? match[3]).toLowerCase();
}

/**
 * Glifo real de la reacción. En admin el campo `emoji` a veces guarda el
 * nombre ("smiley") y el glifo va en los alias (😃), así que se busca el
 * primero que de verdad sea un emoji.
 */
export function reactionGlyph(reaction: ReactionDefinition): string | null {
  if (isEmojiGlyph(reaction.emoji)) return reaction.emoji;
  return (reaction.aliases ?? []).find((alias) => isEmojiGlyph(alias)) ?? null;
}

function normalizeKey(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

export function shortcodeKeys(reaction: ReactionDefinition): string[] {
  const keys = new Set<string>();
  for (const raw of [reaction.slug, reaction.label]) {
    const key = raw ? normalizeKey(raw) : "";
    if (/^[a-z0-9_+-]{2,40}$/.test(key)) keys.add(key);
  }
  return Array.from(keys);
}

export function buildShortcodeMap(list: readonly ReactionDefinition[]): ShortcodeReactionMap {
  const map = new Map<string, ReactionDefinition>();
  for (const reaction of list) {
    for (const key of shortcodeKeys(reaction)) {
      if (!map.has(key)) map.set(key, reaction);
    }
  }
  return map;
}

/** Shortcode canónico que se muestra como tooltip (`:smiley:`). */
export function reactionShortcode(reaction: ReactionDefinition): string {
  const key = shortcodeKeys(reaction)[0];
  return key ? `:${key}:` : reactionGlyph(reaction) ?? reaction.emoji;
}

/**
 * Parte el texto en tramos de texto plano y tramos que son una reacción
 * conocida (por glifo, alias o shortcode). Los tramos de texto adyacentes se
 * fusionan para que el render quede compacto.
 */
export function tokenizeReactionText(
  input: string,
  byEmoji: EmojiReactionMap,
  byShortcode: ShortcodeReactionMap
): ReactionToken[] {
  const out: ReactionToken[] = [];
  const pushText = (text: string) => {
    if (!text) return;
    const last = out[out.length - 1];
    if (last && !last.reaction) last.text += text;
    else out.push({ text });
  };
  const pushPlain = (text: string) => {
    if (!text) return;
    for (const seg of splitTextForReactions(text)) {
      const reaction = seg.isEmoji ? byEmoji.get(seg.text) : undefined;
      if (reaction) out.push({ text: seg.text, reaction });
      else pushText(seg.text);
    }
  };

  let last = 0;
  const re = new RegExp(SHORTCODE_RE.source, SHORTCODE_RE.flags);
  let match: RegExpExecArray | null;
  while ((match = re.exec(input))) {
    const reaction = byShortcode.get(matchKey(match));
    if (!reaction) {
      // `:a:smiley:` — reintenta desde el siguiente carácter.
      re.lastIndex = match.index + 1;
      continue;
    }
    pushPlain(input.slice(last, match.index));
    out.push({ text: match[0], reaction });
    last = match.index + match[0].length;
  }
  pushPlain(input.slice(last));
  return out;
}

/**
 * Reemplaza `:slug:` / `(slug)` / `{slug}` por el glifo de la reacción. Si la
 * reacción no tiene glifo se deja el shortcode (igual se pinta como emoji).
 */
export function replaceShortcodesWithEmoji(
  input: string,
  byShortcode: ShortcodeReactionMap
): string {
  if (!input || byShortcode.size === 0) return input;
  const re = new RegExp(SHORTCODE_RE.source, SHORTCODE_RE.flags);
  let out = "";
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(input))) {
    const reaction = byShortcode.get(matchKey(match));
    const glyph = reaction ? reactionGlyph(reaction) : null;
    if (!glyph) {
      re.lastIndex = match.index + 1;
      continue;
    }
    out += input.slice(last, match.index) + glyph;
    last = match.index + match[0].length;
  }
  return out + input.slice(last);
}
